import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, ChevronRight, Heart } from 'lucide-react';
import BottomNav from '../components/BottomNav';

const Aigrette = ({ className, size = 12, color = '#F79AAE' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="0.9" strokeLinecap="round" aria-hidden="true">
    <path d="M7 13 L7 6" />
    <path d="M7 6 L3 1.5 M7 6 L5 1 M7 6 L7 0.5 M7 6 L9 1 M7 6 L11 1.5" />
  </svg>
);

const AigrettesStyle = () => (
  <style>{`
    @keyframes apAigDrift1 { 0%,100% { transform: translate(0,0) rotate(0deg); } 50% { transform: translate(-3px,-7px) rotate(-6deg); } }
    @keyframes apAigDrift2 { 0%,100% { transform: translate(0,0) rotate(0deg); } 50% { transform: translate(4px,-5px) rotate(5deg); } }
    @keyframes apAigDrift3 { 0%,100% { transform: translate(0,0) rotate(0deg); } 50% { transform: translate(-2px,5px) rotate(4deg); } }
    .ap-aig-1 { animation: apAigDrift1 6s ease-in-out infinite; }
    .ap-aig-2 { animation: apAigDrift2 7.5s ease-in-out infinite 0.8s; }
    .ap-aig-3 { animation: apAigDrift3 9s ease-in-out infinite 1.6s; }
    @media (prefers-reduced-motion: reduce) {
      .ap-aig-1, .ap-aig-2, .ap-aig-3 { animation: none; }
    }
  `}</style>
);

const PageShell = ({ onBack, signed, children }) => (
  <div className="lato-screen min-h-screen bg-[#FAF9F6] pb-24">
    <div
      className="max-w-lg mx-auto px-5 py-8 flex flex-col"
      style={{ minHeight: 'calc(100dvh - 96px)' }}
    >
      <div className="flex items-center mb-6">
        <button
          onClick={onBack}
          className="p-2 -ml-2 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Retour"
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
        </button>
      </div>
      {children}
      {signed && (
        <p
          className="text-center text-[11px] font-light"
          style={{ marginTop: 'auto', paddingTop: 32, color: 'rgba(247,154,174,0.55)' }}
          data-testid="page-signature"
        >
          Spiritu’L
        </p>
      )}
    </div>
    <BottomNav />
  </div>
);

const MiniDandelion = () => (
  <svg width="34" height="34" viewBox="0 0 14 14" fill="none" stroke="#D9788F" strokeWidth="0.7" strokeLinecap="round" aria-hidden="true">
    <path d="M7 13 L7 6" />
    <path d="M7 6 L3 1.5 M7 6 L5 1 M7 6 L7 0.5 M7 6 L9 1 M7 6 L11 1.5" />
    <circle cx="7" cy="6" r="0.7" fill="#D9788F" stroke="none" />
  </svg>
);

const HubCard = ({ icon, title, subtitle, onClick, tinted, testId }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-4 text-left rounded-[24px] px-5 py-5 mb-5 transition-shadow hover:shadow-md shadow-[0_2px_10px_rgba(0,0,0,0.03)]"
    style={{ background: tinted ? 'linear-gradient(135deg, #FDEEF2 0%, #FBE4EA 100%)' : '#FFFDF6', border: '1px solid rgba(243,238,229,0.7)' }}
    data-testid={testId}
  >
    <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 bg-white/70" style={{ border: '1px solid rgba(217,120,143,0.35)' }}>
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[15.5px] text-gray-800" style={{ fontWeight: 500 }}>{title}</p>
      <p className="text-[12.5px] text-gray-500 font-light mt-1 leading-snug">{subtitle}</p>
    </div>
    <ChevronRight size={17} className="text-[#D9788F]/70 flex-shrink-0" />
  </button>
);

const AProposPage = () => {
  const navigate = useNavigate();
  return (
    <PageShell onBack={() => navigate('/profil')} signed>
      <AigrettesStyle />
      {/* Title + aigrettes discrètes */}
      <div className="text-center mb-10 relative">
        <div className="relative inline-block">
          <h1 className="text-2xl font-light text-gray-800">
            À propos de Sp<span className="text-[#F79AAE]">i</span>ritu’L
          </h1>
          <Aigrette className="ap-aig-1 absolute -top-4 -left-4" />
          <Aigrette className="ap-aig-2 absolute -top-3 -right-5" size={10} color="#444444" />
          <Aigrette className="ap-aig-3 absolute -bottom-3 right-8" size={10} />
        </div>
        <div className="mt-2 w-12 h-[2px] bg-[#F79AAE] mx-auto"></div>
      </div>

      <HubCard
        tinted
        icon={<MiniDandelion />}
        title="Notre histoire & notre intention"
        subtitle="Découvrir pourquoi Spiritu’L est née et ce qu’elle souhaite transmettre."
        onClick={() => navigate('/a-propos/histoire')}
        testId="apropos-card-histoire"
      />
      <HubCard
        icon={<Heart size={22} strokeWidth={1.3} className="text-[#D9788F]" />}
        title="Remerciements"
        subtitle="Celles et ceux qui ont contribué à cette aventure."
        onClick={() => navigate('/a-propos/remerciements')}
        testId="apropos-card-remerciements"
      />
    </PageShell>
  );
};

export const NotreHistoirePage = () => {
  const navigate = useNavigate();
  return (
    <PageShell onBack={() => navigate('/a-propos')}>
      <AigrettesStyle />
      <div className="text-center mb-10">
        <div className="relative inline-block">
          <h1 className="text-2xl font-light text-gray-800">Notre histoire &amp; notre intention</h1>
          <Aigrette className="ap-aig-1 absolute -top-4 -left-4" size={11} />
          <Aigrette className="ap-aig-2 absolute -top-3 -right-5" size={9} color="#444444" />
        </div>
        <div className="mt-2 w-12 h-[2px] bg-[#F79AAE] mx-auto"></div>
      </div>

      <div
        className="bg-[#FFFDF6] rounded-[24px] px-6 py-7 shadow-[0_1px_6px_rgba(0,0,0,0.02)] text-left"
        style={{ border: '1px solid rgba(243,238,229,0.5)' }}
        data-testid="section-histoire"
      >
        <h2 className="text-[17px] text-[#D9788F]" style={{ fontWeight: 600, marginBottom: 12 }}>Notre histoire</h2>
        <p className="text-[15px] text-gray-600" style={{ fontWeight: 400, lineHeight: 1.6 }}>
          Spiritu’L est née d’un constat simple : le bien-être mental est essentiel à l’épanouissement, et la méditation peut être un outil précieux pour en prendre soin.
        </p>
        <p className="text-[15px] text-gray-600" style={{ fontWeight: 400, lineHeight: 1.6, marginTop: 18 }}>
          Pourtant, les méditations proposées aujourd’hui suivent souvent une approche laïque. Elles laissent peu de place à la spiritualité, alors qu’elle occupe une place centrale dans la vie de nombreux musulmans.
        </p>
        <p className="text-[15px] text-gray-600" style={{ fontWeight: 400, lineHeight: 1.6, marginTop: 18 }}>
          Spiritu’L est née pour combler ce besoin : créer un espace simple et accessible où méditation et spiritualité musulmane peuvent enfin se rencontrer.
        </p>

        <h2 className="text-[17px] text-[#D9788F]" style={{ fontWeight: 600, marginTop: 30, marginBottom: 12 }} data-testid="section-intention">Notre intention</h2>
        <p className="text-[15px] text-gray-600" style={{ fontWeight: 400, lineHeight: 1.6 }}>
          Proposer des pratiques douces, accessibles et ancrées dans la foi, pour prendre soin de son équilibre intérieur et se rendre disponible à Sa présence.
        </p>
        <p className="text-[15px] text-gray-600" style={{ fontWeight: 400, lineHeight: 1.6, marginTop: 18 }}>
          Spiritu’L n’est ni un enseignement religieux ni une application de récitation du Coran. Elle offre un espace pour ralentir, respirer et cultiver davantage de calme et de présence au quotidien.
        </p>
      </div>
    </PageShell>
  );
};

export const RemerciementsPage = () => {
  const navigate = useNavigate();
  return (
    <PageShell onBack={() => navigate('/a-propos')}>
      <AigrettesStyle />
      <div className="text-center mb-10">
        <div className="relative inline-block">
          <h1 className="text-2xl font-light text-gray-800">Remerciements</h1>
          <Aigrette className="ap-aig-1 absolute -top-4 -left-4" size={11} />
          <Aigrette className="ap-aig-3 absolute -bottom-3 -right-4" size={9} color="#444444" />
        </div>
        <div className="mt-2 w-12 h-[2px] bg-[#F79AAE] mx-auto"></div>
      </div>

      <div className="bg-[#FFFDF6] rounded-[24px] px-6 py-6 shadow-[0_2px_10px_rgba(0,0,0,0.03)]" style={{ border: '1px solid rgba(243,238,229,0.7)' }}>
        <h2 className="text-sm uppercase tracking-wider text-gray-500 mb-4">
          Sources d'inspiration
        </h2>
        <p className="text-sm text-gray-600 leading-relaxed">
          Merci au professeur <span className="font-medium text-gray-800">Raouti</span>, dont les enseignements diffusés par Rahma TV ont inspiré Spiritu’L. Toute erreur ou interprétation présente dans l'application relève uniquement de ma responsabilité.
        </p>
        <a
          href="https://rahma-tv.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-5 px-5 py-2 rounded-full text-[13px] font-light transition-colors hover:bg-[#F79AAE]/10"
          style={{ border: '1px solid rgba(247,154,174,0.5)', background: 'rgba(247,154,174,0.05)', color: '#D9788F' }}
          data-testid="link-decouvrir-rahmatv"
        >
          Découvrir Rahma TV
          <ExternalLink size={12} aria-hidden="true" />
        </a>
        <p className="text-sm text-gray-600 leading-relaxed mt-6" data-testid="credit-aziz">
          Un grand merci à <span className="font-medium text-gray-800">Aziz</span> et à <span className="font-medium text-gray-800">Bilal</span>, les voix qui font vibrer nos dhikrs ✨
        </p>
        <p className="text-sm text-gray-600 leading-relaxed mt-4" data-testid="credit-woury">
          Merci à <span className="font-medium text-gray-800">Woury</span>, mu'allima, ainsi qu'à une précieuse contributrice, pour leur aide dans le classement et le référencement des Noms divins ✨
        </p>
        <p className="text-sm text-gray-600 leading-relaxed mt-4" data-testid="credit-meryem">
          À <span className="font-medium text-gray-800">Meryem</span>, orthophoniste, pour son accompagnement attentif autour de l’articulation et de la justesse des mots.
        </p>
        <a
          href="https://kezadrimeriem33.wixsite.com/consultations-orthop"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-5 px-5 py-2 rounded-full text-[13px] font-light transition-colors hover:bg-[#F79AAE]/10"
          style={{ border: '1px solid rgba(247,154,174,0.5)', background: 'rgba(247,154,174,0.05)', color: '#D9788F' }}
          data-testid="link-meryem-accompagnement"
        >
          Découvrir son accompagnement
          <ExternalLink size={12} aria-hidden="true" />
        </a>
        <p className="text-sm text-gray-600 leading-relaxed mt-6" data-testid="credit-ali-siham-letitia">
          Merci également à <span className="font-medium text-gray-800">Ali</span>, développeur web, pour ses précieux conseils et le temps qu’il m’a accordé, à son épouse <span className="font-medium text-gray-800">Siham</span> pour sa force et son soutien, ainsi qu’à <span className="font-medium text-gray-800">Letitia</span> pour son écoute attentive et ses corrections des audios.
        </p>
      </div>
    </PageShell>
  );
};

export default AProposPage;
